import { useState, useEffect } from 'react'
import { api } from '../api/client'
import { useWorkspaceContext } from '../hooks/useWorkspaceContext'
import Tabs from '../components/Tabs'

interface ROISummary {
  totalSpendUSD: number; totalRevenueUSD: number; roi: number
  totalPosts: number; totalEngagement: number; totalReach: number
  periodStart: string; periodEnd: string
}

interface ChannelROI {
  platformName: string; spendUSD: number; revenueUSD: number
  roi: number; posts: number; engagement: number; reach: number
}

interface Report {
  reportID: number; name: string; reportTypeID: number; reportTypeName?: string
  scheduleFrequency?: string; lastGeneratedAt?: string; isWhiteLabel: boolean
  createdAt: string
}

const TABS = [
  { id: 'roi', label: 'ROI Dashboard' },
  { id: 'reports', label: 'Reports' },
  { id: 'whitelabel', label: 'White Label' },
]

const COLORS = ['#1b6ec2', '#27ae60', '#e74c3c', '#f39c12', '#8e44ad', '#2c3e50']

export default function Reports() {
  const { activeWorkspace } = useWorkspaceContext()
  const wsId = activeWorkspace?.workspaceID ?? 0
  const [tab, setTab] = useState('roi')
  const [days, setDays] = useState(30)
  const [roi, setRoi] = useState<ROISummary | null>(null)
  const [channelRoi, setChannelRoi] = useState<ChannelROI[]>([])
  const [reports, setReports] = useState<Report[]>([])
  const [msg, setMsg] = useState('')

  useEffect(() => { loadROI() }, [days])
  useEffect(() => { loadReports() }, [])

  const loadROI = async () => {
    try {
      const [summary, channels] = await Promise.all([
        api.get<ROISummary>(`/workspaces/${wsId}/reports/roi?days=${days}`),
        api.get<ChannelROI[]>(`/workspaces/${wsId}/reports/roi/channels?days=${days}`),
      ])
      setRoi(summary); setChannelRoi(channels)
    } catch (err) { console.error('[SMM]', err);
      setRoi({ totalSpendUSD: 0, totalRevenueUSD: 0, roi: 0, totalPosts: 0, totalEngagement: 0, totalReach: 0, periodStart: '', periodEnd: '' })
      setChannelRoi([])
    }
  }

  const loadReports = async () => {
    try { setReports(await api.get<Report[]>(`/workspaces/${wsId}/reports`)) } catch (err) { console.error('[SMM]', err) }
  }

  const handleGenerateReport = async (type: string, whiteLabel: boolean) => {
    try {
      await api.post(`/workspaces/${wsId}/reports/generate`, { reportType: type, days, isWhiteLabel: whiteLabel })
      setMsg(`${type} report queued for generation`); loadReports()
    } catch (err) { console.error('[SMM]', err); setMsg('Failed to generate report') }
  }

  const usd = (v: number) => `$${v.toFixed(2)}`
  const maxSpend = Math.max(...channelRoi.map(c => c.spendUSD), 1)

  return (
    <div className="smm-reports">
      <h1>Reports &amp; ROI</h1>
      {msg && <div className="smm-success-card">{msg}</div>}
      <Tabs tabs={TABS} activeTab={tab} onTabChange={setTab} />

      {tab === 'roi' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <select className="smm-select" style={{ width: 'auto' }} value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last year</option>
            </select>
          </div>
          {roi && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="smm-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{usd(roi.totalSpendUSD)}</div><div className="smm-hint">Total Spend</div>
                </div>
                <div className="smm-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{usd(roi.totalRevenueUSD)}</div><div className="smm-hint">Est. Revenue</div>
                </div>
                <div className="smm-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: roi.roi >= 0 ? '#27ae60' : '#e74c3c' }}>{roi.roi.toFixed(1)}%</div><div className="smm-hint">ROI</div>
                </div>
                <div className="smm-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{roi.totalPosts}</div><div className="smm-hint">Posts</div>
                </div>
                <div className="smm-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{roi.totalEngagement.toLocaleString()}</div><div className="smm-hint">Engagement</div>
                </div>
                <div className="smm-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{roi.totalReach.toLocaleString()}</div><div className="smm-hint">Reach</div>
                </div>
              </div>

              <div className="smm-card">
                <h3>Spend by Channel</h3>
                {channelRoi.length > 0 ? (
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {channelRoi.map((c, i) => (
                      <div key={c.platformName} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ width: 100, fontWeight: 500, fontSize: '0.85rem' }}>{c.platformName}</div>
                        <div style={{ flex: 1, height: 24, background: '#f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(c.spendUSD / maxSpend) * 100}%`, background: COLORS[i % COLORS.length], borderRadius: 8 }} />
                        </div>
                        <span style={{ fontWeight: 600, minWidth: 80, textAlign: 'right' }}>{usd(c.spendUSD)}</span>
                        <span style={{ minWidth: 60, textAlign: 'right', color: c.roi >= 0 ? '#27ae60' : '#e74c3c', fontWeight: 500 }}>{c.roi.toFixed(0)}% ROI</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="smm-hint">No channel data available for this period.</p>}
              </div>

              {channelRoi.length > 0 && (
                <div className="smm-card">
                  <h3>Channel Comparison</h3>
                  <table className="smm-table">
                    <thead><tr><th>Channel</th><th>Spend</th><th>Revenue</th><th>ROI</th><th>Posts</th><th>Engagement</th><th>CPE</th></tr></thead>
                    <tbody>{channelRoi.map(c => {
                      const cpe = c.engagement > 0 ? c.spendUSD / c.engagement : 0
                      return <tr key={c.platformName}><td><strong>{c.platformName}</strong></td><td>{usd(c.spendUSD)}</td><td>{usd(c.revenueUSD)}</td>
                        <td style={{ color: c.roi >= 0 ? '#27ae60' : '#e74c3c' }}>{c.roi.toFixed(1)}%</td><td>{c.posts}</td><td>{c.engagement.toLocaleString()}</td><td>{usd(cpe)}</td></tr>
                    })}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === 'reports' && (
        <div className="smm-card">
          <h3>Generated Reports</h3>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button className="smm-btn smm-btn-primary" onClick={() => handleGenerateReport('Monthly Performance', false)}>Generate Monthly Performance</button>
            <button className="smm-btn" onClick={() => handleGenerateReport('ROI Summary', false)}>Generate ROI Summary</button>
            <button className="smm-btn" onClick={() => handleGenerateReport('Engagement Report', false)}>Generate Engagement Report</button>
          </div>
          {reports.length > 0 ? (
            <table className="smm-table">
              <thead><tr><th>Name</th><th>Type</th><th>Schedule</th><th>Last Generated</th><th>White Label</th></tr></thead>
              <tbody>{reports.map(r => (
                <tr key={r.reportID}><td><strong>{r.name}</strong></td><td>{r.reportTypeName || `Type ${r.reportTypeID}`}</td>
                  <td>{r.scheduleFrequency || 'Manual'}</td><td>{r.lastGeneratedAt ? new Date(r.lastGeneratedAt).toLocaleDateString() : '-'}</td>
                  <td>{r.isWhiteLabel ? <span className="smm-badge smm-badge-published">Yes</span> : 'No'}</td></tr>
              ))}</tbody>
            </table>
          ) : <p className="smm-hint">No reports generated yet. Use the buttons above to create your first report.</p>}
        </div>
      )}

      {tab === 'whitelabel' && (
        <div className="smm-card">
          <h3>White Label Reports</h3>
          <p className="smm-hint">Generate client-ready reports with your branding. White label reports remove Social Media Manager branding and use your brand kit colors and logo.</p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button className="smm-btn smm-btn-primary" onClick={() => handleGenerateReport('Client Performance Report', true)}>Generate Client Report</button>
            <button className="smm-btn" onClick={() => handleGenerateReport('Agency Overview', true)}>Generate Agency Overview</button>
          </div>
          {reports.filter(r => r.isWhiteLabel).length > 0 ? (
            <table className="smm-table">
              <thead><tr><th>Name</th><th>Created</th><th>Last Generated</th></tr></thead>
              <tbody>{reports.filter(r => r.isWhiteLabel).map(r => (
                <tr key={r.reportID}><td><strong>{r.name}</strong></td><td>{new Date(r.createdAt).toLocaleDateString()}</td><td>{r.lastGeneratedAt ? new Date(r.lastGeneratedAt).toLocaleDateString() : '-'}</td></tr>
              ))}</tbody>
            </table>
          ) : <p className="smm-hint">No white label reports yet.</p>}
        </div>
      )}
    </div>
  )
}
